import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from './MobileApp';
import { useSettings } from '../../context/SettingsContext';
import { mobileApi } from '../../services/api';
import MobileHeader from '../../components/mobile/MobileHeader';
import MobileCard from '../../components/mobile/MobileCard';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  Search,
  Package,
  ChevronRight,
  Loader2,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const MobileScanner = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getToken } = useMobileAuth();
  const { formatDate } = useSettings();

  const action = searchParams.get('action'); // 'inspect' | null
  const templateId = searchParams.get('template');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recentScans, setRecentScans] = useState([]);

  useEffect(() => {
    const stored = localStorage.getItem('mobile_recent_scans');
    if (stored) {
      try { setRecentScans(JSON.parse(stored)); } catch {}
    }
  }, []);

  const handleSearch = async (q) => {
    setQuery(q);
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const token = getToken();
      const res = await mobileApi.searchAssets(q, token);
      setResults(res.data?.matches || []);
    } catch { setResults([]); }
    finally { setSearching(false); }
  };

  const handleSelect = (asset) => {
    // Save to recent scans
    const updated = [
      { id: asset.id, unit_number: asset.unit_number, name: asset.name, time: new Date().toISOString() },
      ...recentScans.filter(s => s.id !== asset.id)
    ].slice(0, 10);
    localStorage.setItem('mobile_recent_scans', JSON.stringify(updated));
    setRecentScans(updated);

    const ret = searchParams.get('return');
    if (ret === 'defect') {
      navigate(`/mobile/defects/new?asset=${asset.id}`);
      return;
    }
    if (action === 'inspect' && templateId) {
      navigate(`/mobile/inspections/new/${asset.id}/${templateId}`);
    } else if (action === 'inspect') {
      navigate(`/mobile/assets/${asset.id}`);
    } else {
      navigate(`/mobile/assets/${asset.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--surface-page)]" data-testid="mobile-scanner-page">
      <MobileHeader title="Find Asset" showBack />

      <div className="px-4 py-4">
        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ink-tertiary)]" />
          <Input
            value={query}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Unit #, Serial #, or Name..."
            className="pl-10 bg-[var(--surface-card)] border-[var(--border)] text-[var(--ink-primary)] h-12"
            autoFocus
            data-testid="scanner-search-input"
          />
        </div>

        {/* Results */}
        {searching && (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 text-[var(--volt-icon)] animate-spin" />
          </div>
        )}

        {results.map(asset => (
          <MobileCard
            key={asset.id}
            className="mb-2"
            onClick={() => handleSelect(asset)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[var(--ink-tertiary)]" />
                <div>
                  <p className="font-semibold text-[var(--ink-primary)]">{asset.unit_number || asset.name}</p>
                  {asset.serial_number && (
                    <p className="text-xs text-[var(--ink-tertiary)]">S/N: {asset.serial_number}</p>
                  )}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-[var(--ink-tertiary)]" />
            </div>
          </MobileCard>
        ))}

        {!searching && query.length >= 2 && results.length === 0 && (
          <p className="text-center text-[var(--ink-tertiary)] py-8">No assets found</p>
        )}

        {/* Recent Scans */}
        {query.length < 2 && recentScans.length > 0 && (
          <div className="mt-4">
            <h2 className="text-sm font-semibold text-[var(--ink-tertiary)] uppercase tracking-wide mb-3">
              Recent
            </h2>
            {recentScans.map(scan => (
              <MobileCard
                key={scan.id}
                className="mb-2"
                padding="small"
                onClick={() => handleSelect(scan)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Package className="w-4 h-4 text-[var(--ink-tertiary)]" />
                    <span className="text-[var(--ink-primary)] text-sm">{scan.unit_number || scan.name}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-[var(--ink-tertiary)]">
                    <Clock className="w-3 h-3" />
                    {formatDate(scan.time)}
                  </div>
                </div>
              </MobileCard>
            ))}
          </div>
        )}

        {query.length < 2 && recentScans.length === 0 && (
          <p className="text-center text-[var(--ink-tertiary)] py-8 text-sm">
            Search for an asset by unit number, serial number, or name
          </p>
        )}
      </div>
    </div>
  );
};

export default MobileScanner;
